import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';
import { NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AutoScrollToDirectiveModule,
  RouterStaticLinkDirectiveModule,
  trackByProp,
} from '@icilalune/utils';
import { MatIconModule } from '@angular/material/icon';
import { TreeItem } from '@nx-icilalune/drupal-types';
import {
  MainMenuItem,
  MainMenuTreeItem,
} from '@nx-icilalune/data/icilalune/types';
import { map, Observable } from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';
import { PushModule } from '@ngrx/component';

interface HorizontalMenuStore {
  breadcrumbs?: MainMenuItem[] | undefined;
}

@Component({
  selector: 'sh-ui-horizontal-menu',
  standalone: true,
  imports: [
    RouterStaticLinkDirectiveModule,
    MatIconModule,
    NgIf,
    NgForOf,
    NgTemplateOutlet,
    PushModule,
    AutoScrollToDirectiveModule,
  ],
  template: `
    <ng-container *ngIf="openMenuTemplate">
      <ng-template [ngTemplateOutlet]="openMenuTemplate"></ng-template>
    </ng-container>
    <ng-container *ngIf="menuItems">
      <ul (scroll)="onScroll()">
        <ng-template
          ngFor
          [ngForOf]="menuItems"
          let-menuItem
          [ngForTrackBy]="trackById"
        >
          <li
            [i2lAutoScrollTo]="
              (getSelected$(menuItem.path) | ngrxPush) || false
            "
            [direction]="'x'"
          >
            <ng-template
              [ngTemplateOutlet]="menuItemTemplate"
              [ngTemplateOutletContext]="{ menuItem: menuItem }"
            ></ng-template>
          </li>
        </ng-template>
        <ng-content select="[settingsLink]"></ng-content>
      </ul>
    </ng-container>

    <ng-template #menuItemTemplate let-menuItem="menuItem">
      <a
        [routerStaticLink]="menuItem.path"
        [class.selected]="getSelected$(menuItem.path) | ngrxPush"
        [class.icon]="menuItem.color === 'terre' || menuItem.color === 'lune'"
        [style.--themeColor]="
          'var(--lune-color-section-' + menuItem.color + '-default)'
        "
      >
        <ng-container *ngIf="menuItem.color === 'lune'">
          <mat-icon svgIcon="icon-star_moon"></mat-icon>
        </ng-container>
        <ng-container *ngIf="menuItem.color === 'terre'">
          <mat-icon svgIcon="icon-star_earth"></mat-icon>
        </ng-container>
        <ng-container
          *ngIf="menuItem.color !== 'terre' && menuItem.color !== 'lune'"
        >
          <span [innerHTML]="menuItem.title"></span>
        </ng-container>
      </a>
    </ng-template>
  `,
  styleUrls: ['./horizontal-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HorizontalMenuComponent extends ComponentStore<HorizontalMenuStore> {
  readonly breadcrumbs$ = this.select((s) => s.breadcrumbs);

  @Input()
  menuItems: TreeItem<MainMenuTreeItem>[] | undefined;

  @Input()
  openMenuTemplate: TemplateRef<any> | undefined;

  @Input()
  set breadcrumbs(breadcrumbs: MainMenuItem[] | undefined) {
    this.patchState({ breadcrumbs });
  }

  @Output()
  menuScroll: EventEmitter<any> = new EventEmitter<any>();

  trackById = trackByProp<TreeItem<MainMenuTreeItem>>('id');

  constructor() {
    super({});
  }

  getSelected$(path: string | undefined): Observable<boolean> {
    return this.breadcrumbs$.pipe(
      map((breadcrumbs) => {
        return !!breadcrumbs?.find((bc) => bc.path === path);
      })
    );
  }

  onScroll() {
    this.menuScroll.emit();
  }
}
